import React, { useState, useMemo } from 'react';
import {
  Table,
  Tag,
  Button,
  Space,
  Tooltip,
  Popconfirm,
  message,
  Dropdown,
  Menu
} from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { OrderData } from '@/shared/api/types';
import { formatDate, formatCurrency, getStatusColor, getStatusText } from '../utils/orderUtils';

export interface OrderListProps {
  /** 订单数据 */
  data: OrderData[];
  /** 是否正在加载 */
  loading?: boolean;
  /** 是否只读模式 */
  readOnly?: boolean;
  /** 是否可以更新状态 */
  canUpdateStatus?: boolean;
  /** 是否可以添加备注 */
  canAddNotes?: boolean;
  /** 是否可以删除订单 */
  canDelete?: boolean;
  /** 是否显示选择列 */
  showSelection?: boolean;
  /** 是否显示操作列 */
  showActions?: boolean;
  /** 刷新回调 */
  onRefresh?: () => void;
  /** 查看详情回调 */
  onViewDetail?: (orderId: string) => void;
  /** 编辑回调 */
  onEdit?: (orderId: string) => void;
  /** 删除回调 */
  onDelete?: (orderId: string) => Promise<void>;
  /** 更新状态回调 */
  onUpdateStatus?: (orderId: string, newStatus: string) => Promise<void>;
  /** 选择变化回调 */
  onSelectionChange?: (selectedRowKeys: React.Key[], selectedRows: OrderData[]) => void;
  /** 行点击回调 */
  onRowClick?: (record: OrderData, index: number) => void;
  /** 自定义列配置 */
  columns?: ColumnsType<OrderData>;
  /** 表格样式 */
  style?: React.CSSProperties;
  /** 表格类名 */
  className?: string;
}

const OrderList: React.FC<OrderListProps> = ({
  data,
  loading = false,
  readOnly = false,
  canUpdateStatus = false,
  canAddNotes = false,
  canDelete = false,
  showSelection = true,
  showActions = true,
  onRefresh,
  onViewDetail,
  onEdit,
  onDelete,
  onUpdateStatus,
  onSelectionChange,
  onRowClick,
  columns: customColumns,
  style,
  className
}) => {
  const { t } = useTranslation(['modules', 'common']);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // 处理行选择
  const handleSelectionChange = (newSelectedRowKeys: React.Key[], selectedRows: OrderData[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    onSelectionChange?.(newSelectedRowKeys, selectedRows);
  };

  // 处理行点击
  const handleRowClick = (record: OrderData, index: number) => {
    onRowClick?.(record, index);
  };

  // 处理查看详情
  const handleViewDetail = (orderId: string) => {
    onViewDetail?.(orderId);
  };

  // 处理编辑
  const handleEdit = (orderId: string) => {
    onEdit?.(orderId);
  };

  // 处理删除
  const handleDelete = async (orderId: string) => {
    try {
      await onDelete?.(orderId);
      message.success(t('modules:order.query.list.deleteSuccess', '删除成功'));
    } catch (error) {
      message.error(t('modules:order.query.list.deleteError', '删除失败'));
    }
  };

  // 处理状态更新
  const handleStatusUpdate = async (orderId: string, newStatus: string) => {
    try {
      await onUpdateStatus?.(orderId, newStatus);
      message.success(t('modules:order.query.list.statusUpdateSuccess', '状态更新成功'));
    } catch (error) {
      message.error(t('modules:order.query.list.statusUpdateError', '状态更新失败'));
    }
  };

  // 默认列配置
  const defaultColumns: ColumnsType<OrderData> = [
    {
      title: t('modules:order.query.list.columns.orderId', '订单ID'),
      dataIndex: 'order_id',
      key: 'order_id',
      width: 120,
      fixed: 'left',
      sorter: (a, b) => a.order_id.localeCompare(b.order_id),
      render: (text: string) => (
        <span
          style={{ fontWeight: 500, color: '#1890ff', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation(); // 防止触发行点击事件
            onViewDetail?.(text);
          }}
        >
          {text}
        </span>
      )
    },
    {
      title: t('modules:order.query.list.columns.status', '状态'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      filters: [
        { text: t('modules:order.query.filters.statusPending', '待处理'), value: 'pending' },
        { text: t('modules:order.query.filters.statusProcessing', '处理中'), value: 'processing' },
        { text: t('modules:order.query.filters.statusShipped', '已发货'), value: 'shipped' },
        { text: t('modules:order.query.filters.statusDelivered', '已送达'), value: 'delivered' },
        { text: t('modules:order.query.filters.statusCancelled', '已取消'), value: 'cancelled' }
      ],
      onFilter: (value, record) => record.status === value,
      render: (status: string) => (
        <Tag color={getStatusColor(status)} style={{ margin: 0 }}>
          {getStatusText(status, t)}
        </Tag>
      )
    },
    {
      title: t('modules:order.query.list.columns.customer', '客户'),
      dataIndex: 'customer_id',
      key: 'customer',
      width: 120,
      render: (customerId: string, record: any) => (
        <span>
          {record.customer_info?.name || t('modules:order.query.customer.unknown', '客户 {id}', { id: customerId.slice(0, 8) })}
        </span>
      )
    },
    {
      title: t('modules:order.query.list.columns.totalAmount', '总金额'),
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 120,
      sorter: (a, b) => a.total_amount - b.total_amount,
      render: (amount: number, record: OrderData) => (
        <span style={{ fontWeight: 500 }}>
          {formatCurrency(amount, record.currency)}
        </span>
      )
    },
    {
      title: t('modules:order.query.list.columns.platform', '平台'),
      dataIndex: 'platform_source',
      key: 'platform',
      width: 100,
      filters: [
        { text: t('modules:order.query.platforms.web', '网站'), value: 'web' },
        { text: t('modules:order.query.platforms.wechat', '微信'), value: 'wechat' },
        { text: t('modules:order.query.platforms.whatsapp', 'WhatsApp'), value: 'whatsapp' },
        { text: t('modules:order.query.platforms.mobile', '手机应用'), value: 'mobile' }
      ],
      onFilter: (value, record) => record.platform_source === value,
      render: (platform: string) => (
        <Tag color="blue">{platform}</Tag>
      )
    },
    {
      title: t('modules:order.query.list.columns.orderDate', '订单日期'),
      dataIndex: 'ordered_at',
      key: 'ordered_at',
      width: 140,
      sorter: (a, b) => new Date(a.ordered_at).getTime() - new Date(b.ordered_at).getTime(),
      render: (date: string) => formatDate(date, 'short')
    },
    {
      title: t('modules:order.query.list.columns.items', '商品数量'),
      key: 'items_count',
      width: 100,
      render: (_, record: OrderData) => (
        <span>{record.items_count || record.items?.length || 0} {t('modules:order.query.units.items', '件')}</span>
      )
    },
    {
      title: t('modules:order.query.list.columns.estimatedDelivery', '预计送达'),
      dataIndex: 'estimated_delivery_date',
      key: 'estimated_delivery_date',
      width: 140,
      render: (date?: string) => (
        date ? formatDate(date, 'short') : '-'
      )
    }
  ];

  // 操作列
  const actionColumn: ColumnsType<OrderData> = [
    {
      title: t('modules:order.query.list.columns.actions', '操作'),
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record: OrderData) => {
        const menuItems = [];

        // 查看详情
        menuItems.push({
          key: 'view',
          label: t('modules:order.query.list.actions.view', '查看详情'),
          icon: <EyeOutlined />,
          onClick: () => handleViewDetail(record.order_id)
        });

        // 编辑（如果不只读）
        if (!readOnly) {
          menuItems.push({
            key: 'edit',
            label: t('modules:order.query.list.actions.edit', '编辑'),
            icon: <EditOutlined />,
            onClick: () => handleEdit(record.order_id)
          });
        }

        // 更新状态（如果有权限）
        if (canUpdateStatus && !readOnly) {
          const statusMenuItems = [
            {
              key: 'mark_processing',
              label: t('modules:order.query.list.actions.markProcessing', '标记为处理中'),
              onClick: () => handleStatusUpdate(record.order_id, 'processing')
            },
            {
              key: 'mark_shipped',
              label: t('modules:order.query.list.actions.markShipped', '标记为已发货'),
              onClick: () => handleStatusUpdate(record.order_id, 'shipped')
            },
            {
              key: 'mark_delivered',
              label: t('modules:order.query.list.actions.markDelivered', '标记为已送达'),
              onClick: () => handleStatusUpdate(record.order_id, 'delivered')
            },
            {
              key: 'mark_cancelled',
              label: t('modules:order.query.list.actions.markCancelled', '标记为已取消'),
              onClick: () => handleStatusUpdate(record.order_id, 'cancelled')
            }
          ];

          menuItems.push({
            key: 'update_status',
            label: t('modules:order.query.list.actions.updateStatus', '更新状态'),
            icon: <CheckCircleOutlined />,
            children: statusMenuItems
          });
        }

        // 删除（如果有权限）
        if (canDelete && !readOnly) {
          menuItems.push({
            key: 'delete',
            label: (
              <Popconfirm
                title={t('modules:order.query.list.actions.deleteConfirm', '确定要删除这个订单吗？')}
                onConfirm={() => handleDelete(record.order_id)}
                okText={t('common:yes', '是')}
                cancelText={t('common:no', '否')}
              >
                <span style={{ color: '#ff4d4f' }}>
                  {t('modules:order.query.list.actions.delete', '删除')}
                </span>
              </Popconfirm>
            ),
            icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />
          });
        }

        return (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record.order_id)}
              title={t('modules:order.query.list.actions.view', '查看详情')}
            />
            {!readOnly && (
              <Dropdown
                menu={{ items: menuItems }}
                trigger={['click']}
                placement="bottomRight"
              >
                <Button
                  type="link"
                  size="small"
                  icon={<MoreOutlined />}
                  title={t('modules:order.query.list.actions.more', '更多操作')}
                />
              </Dropdown>
            )}
          </Space>
        );
      }
    }
  ];

  // 选择列配置
  const selectionColumn = {
    key: 'selection',
    width: 60,
    fixed: 'left' as const,
    render: (_: unknown, record: OrderData) => (
      <input
        type="checkbox"
        checked={selectedRowKeys.includes(record.order_id)}
        onChange={(e) => {
          const checked = e.target.checked;
          const newSelectedRowKeys = checked
            ? [...selectedRowKeys, record.order_id]
            : selectedRowKeys.filter(key => key !== record.order_id);
          const selectedRows = data.filter(row => newSelectedRowKeys.includes(row.order_id));
          handleSelectionChange(newSelectedRowKeys, selectedRows);
        }}
        onClick={(e) => e.stopPropagation()}
      />
    )
  };

  // 构建最终的列配置
  const finalColumns = useMemo(() => {
    const cols: ColumnsType<OrderData> = [];

    // 添加选择列（如果需要）
    if (showSelection && !readOnly) {
      cols.push(selectionColumn as ColumnsType<OrderData>[number]);
    }

    // 添加数据列
    if (customColumns && customColumns.length > 0) {
      cols.push(...customColumns);
    } else {
      cols.push(...defaultColumns);
    }

    // 添加操作列（如果需要）
    if (showActions) {
      cols.push(...actionColumn);
    }

    return cols;
  }, [showSelection, readOnly, customColumns, defaultColumns, showActions, actionColumn, selectionColumn]);

  // 表格行配置
  const rowSelection: TableProps<OrderData>['rowSelection'] = showSelection && !readOnly ? {
    selectedRowKeys,
    onChange: handleSelectionChange,
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE
    ]
  } : undefined;

  // 表格行属性
  const rowProps = (record: OrderData, index?: number) => {
    const safeIndex = index ?? 0; // 提供默认值
    return {
      onClick: () => handleRowClick(record, safeIndex),
      style: {
        cursor: 'pointer',
        backgroundColor: selectedRowKeys.includes(record.order_id) ? '#f0f7ff' : 'inherit'
      }
    };
  };

  return (
    <div className="order-list-container">
      {/* 工具栏 */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ marginRight: 16 }}>
            {t('modules:order.query.list.total', '共')} {data.length} {t('modules:order.query.list.orders', '条订单')}
          </span>
          {selectedRowKeys.length > 0 && (
            <span>
              {t('modules:order.query.list.selected', '已选择')} {selectedRowKeys.length} {t('modules:order.query.list.orders', '条订单')}
            </span>
          )}
        </div>
        <div>
          {onRefresh && (
            <Button
              icon={<ReloadOutlined />}
              onClick={onRefresh}
              loading={loading}
              size="small"
            >
              {t('modules:order.query.list.refresh', '刷新')}
            </Button>
          )}
        </div>
      </div>

      {/* 表格 */}
      <Table<OrderData>
        columns={finalColumns}
        dataSource={data}
        rowKey="order_id"
        loading={loading}
        rowSelection={rowSelection}
        onRow={rowProps}
        scroll={{ x: 'max-content' }}
        pagination={false}
        style={style}
        className={className}
        size="middle"
        bordered
      />
    </div>
  );
};

export default OrderList;